/**
 * OutlinedCard component for the Indigo design system.
 *
 * A lightweight bordered panel (border + radius, transparent background, no
 * elevation) with an optional title. Used for grouped read-only content inside
 * a larger surface — e.g. the "Key events" columns on the Case Overview.
 *
 * The title (a level-3 `Heading`) sits flush-left; the body is indented so it
 * reads as content under the heading.
 *
 * @example
 * ```tsx
 * <OutlinedCard title="Right Shoulder">
 *   <ul>…bulleted key events…</ul>
 * </OutlinedCard>
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, type ReactNode } from "react";
import { Heading } from "../Typography/Heading";
import "./OutlinedCard.css";

export interface OutlinedCardProps {
  /** Optional title rendered at the top of the card. */
  title?: ReactNode;

  /** Card body content. */
  children?: ReactNode;

  /** Additional CSS classes applied to the card container. */
  className?: string;

  /** Additional CSS classes applied to the body wrapper. */
  contentClassName?: string;
}

export const OutlinedCard = forwardRef<HTMLDivElement, OutlinedCardProps>(
  ({ title, children, className, contentClassName }, ref) => {
    return (
      <div ref={ref} className={clsx("indigo-outlined-card", className)}>
        {title && (
          <Heading
            level={3}
            className="indigo-outlined-card__title text-indigo-text-secondary"
          >
            {title}
          </Heading>
        )}
        <div className={clsx("indigo-outlined-card__body", contentClassName)}>
          {children}
        </div>
      </div>
    );
  },
);

OutlinedCard.displayName = "OutlinedCard";
